import React, { useMemo, useState, useContext } from "react";
import { useHistory } from "react-router";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import DataTable from "react-data-table-component";
import CreateIcon from "@mui/icons-material/Create";
import GetAppIcon from "@mui/icons-material/GetApp";
import useConsentData from "../../queries/consent/getConsentData";
import useMarketingConsentData from "../../queries/consent/getMarketingConsentData";
import Loader from "../../components/Loader";
import { AuthContext } from "../../utils/userProvider";
import { exportToCSV, exportMarketingConsentToCSV } from "../../utils/csvExport";
import moment from "moment";
import Swal from "sweetalert2";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
} from "../../components/ListPage";

const ConsentData = () => {
  const { authState } = useContext(AuthContext);
  const history = useHistory();

  const [filterText, setFilterText] = useState("");
  const [marketingFilterText, setMarketingFilterText] = useState("");
  const [dateFilter, setDateFilter] = useState("12months"); // Default to last 12 months
  const [response] = useConsentData(dateFilter);
  const [marketingResponse] = useMarketingConsentData(dateFilter);

  const columns = [
    { name: "Consent Date", selector: "consent_date", sortable: true, cell: (row) => row.consent_date ? moment(row.consent_date).format("DD MMM YYYY, HH:mm") : "-" },
    { name: "First Name", selector: "first_name", sortable: true },
    { name: "Last Name", selector: "last_name", sortable: true },
    { name: "Email", selector: "email_id", sortable: true },
    { name: "Mobile", selector: "mobile_number", sortable: true },
    { name: "Expiry Date", selector: "expiry_date", sortable: true, cell: (row) => row.expiry_date ? moment(row.expiry_date).format("DD MMM YYYY") : "-" },
    { name: "Withdrawn Date", selector: "consent_withdrawn_date", sortable: true, cell: (row) => row.consent_withdrawn_date ? moment(row.consent_withdrawn_date).format("DD MMM YYYY, HH:mm") : "-" },
    { name: "Remarks", selector: "remarks", sortable: false, cell: (row) => row.remarks || "-" },
    {
      name: "Options",
      cell: (row) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<CreateIcon />}
          onClick={() => history.push(`/dashboard/consent-data/edit/${row.id}`)}
        >
          Edit
        </Button>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const marketingColumns = [
    { name: "Consent Date", selector: "consent_date", sortable: true, cell: (row) => row.consent_date ? moment(row.consent_date).format("DD MMM YYYY, HH:mm") : "-" },
    { name: "First Name", selector: "first_name", sortable: true },
    { name: "Last Name", selector: "last_name", sortable: true },
    { name: "Email", selector: "email_id", sortable: true },
    { name: "Mobile", selector: "mobile_number", sortable: true },
    { name: "Expiry Date", selector: "expiry_date", sortable: true, cell: (row) => row.expiry_date ? moment(row.expiry_date).format("DD MMM YYYY") : "-" },
    { name: "Rec Information", selector: "marketing_opt_in", sortable: true, cell: (row) => row.marketing_opt_in ? "Yes" : "No" },
    { name: "Withdrawn Date", selector: "consent_withdrawn_date", sortable: true, cell: (row) => row.consent_withdrawn_date ? moment(row.consent_withdrawn_date).format("DD MMM YYYY, HH:mm") : "-" },
    { name: "Remarks", selector: "remarks", sortable: false, cell: (row) => row.remarks || "-" },
    {
      name: "Options",
      cell: (row) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<CreateIcon />}
          onClick={() => history.push(`/dashboard/consent-data/edit/${row.id}`)}
        >
          Edit
        </Button>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const filteredItems = useMemo(() => {
    let items = Array.isArray(response.data) ? response.data : [];
    
    // Apply text search filter (date filtering is now handled on backend)
    if (filterText) {
      const q = filterText.toLowerCase();
      items = items.filter((it) =>
        (it.first_name && it.first_name.toLowerCase().includes(q)) ||
        (it.last_name && it.last_name.toLowerCase().includes(q)) ||
        (it.email_id && it.email_id.toLowerCase().includes(q)) ||
        (it.mobile_number && it.mobile_number.toLowerCase().includes(q))
      );
    }
    
    return items;
  }, [response.data, filterText]);

  const filteredMarketingItems = useMemo(() => {
    let items = Array.isArray(marketingResponse.data) ? marketingResponse.data : [];
    
    if (marketingFilterText) {
      const q = marketingFilterText.toLowerCase();
      items = items.filter((it) =>
        (it.first_name && it.first_name.toLowerCase().includes(q)) ||
        (it.last_name && it.last_name.toLowerCase().includes(q)) ||
        (it.email_id && it.email_id.toLowerCase().includes(q)) ||
        (it.mobile_number && it.mobile_number.toLowerCase().includes(q))
      );
    }
    
    return items;
  }, [marketingResponse.data, marketingFilterText]);


  const handleExportToCSV = () => {
    if (!response.data || response.data.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No consent data available to export.'
      });
      return;
    }

    exportToCSV(filteredItems, `consent-data-${new Date().toISOString().split('T')[0]}.csv`);
    Swal.fire({
      icon: 'success',
      title: 'Export Successful',
      text: 'Data has been downloaded as CSV file.',
      timer: 2000
    });
  };

  const handleExportMarketingToCSV = () => {
    if (!marketingResponse.data || marketingResponse.data.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No marketing consent data available to export.'
      });
      return;
    }

    exportMarketingConsentToCSV(filteredMarketingItems, `marketing-consent-data-${new Date().toISOString().split('T')[0]}.csv`);
    Swal.fire({
      icon: 'success',
      title: 'Export Successful',
      text: 'Marketing consent data has been downloaded as CSV file.',
      timer: 2000
    });
  };

  const isLoading = response.loading || marketingResponse.loading;

  return (
    <ListPageShell>
      <ListPageCard>
        {isLoading && <Loader />}
        {!isLoading && (
          <div>
            <ListPageHeader
              title="Consent Data"
              actions={
                <>
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Date Filter</InputLabel>
                    <Select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      label="Date Filter"
                    >
                      <MenuItem value="12months">Last 12 Months</MenuItem>
                      <MenuItem value="2years">Last 2 Years</MenuItem>
                      <MenuItem value="all">All</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    id="filter-text"
                    onChange={(e) => setFilterText(e.target.value)}
                    label="Search"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleExportToCSV}
                    disabled={!response.data || response.data.length === 0}
                    size="small"
                  >
                    Export to CSV
                  </Button>
                </>
              }
            />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 30]}
              noHeader={true}
              data={filteredItems}
              columns={columns}
              highlightOnHover
              customStyles={dataTableCustomStyles}
            />

            <Box sx={{ mt: 5 }}>
              <ListPageHeader
                title="Marketing Consent Data"
                actions={
                  <>
                    <TextField
                      id="marketing-filter-text"
                      onChange={(e) => setMarketingFilterText(e.target.value)}
                      label="Search"
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 200 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<GetAppIcon />}
                      onClick={handleExportMarketingToCSV}
                      disabled={!marketingResponse.data || marketingResponse.data.length === 0}
                      size="small"
                    >
                      Export to CSV
                    </Button>
                  </>
                }
              />
              <DataTable
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 30]}
                noHeader={true}
                data={filteredMarketingItems}
                columns={marketingColumns}
                highlightOnHover
                customStyles={dataTableCustomStyles}
              />
            </Box>
          </div>
        )}
      </ListPageCard>
    </ListPageShell>
  );
};

export default ConsentData;
